/**
 * Pickups Feature Constants
 * All hard-coded values, configuration options, and enums for the pickups feature
 */

// ============================================================================
// PICKUP STATUS OPTIONS
// ============================================================================

/**
 * Pickup status options
 * Use translation keys for labels: t(`pickups.status.${value}`)
 */
export const PICKUP_STATUS_OPTIONS = [
  { value: "pending", label: "pickups.status.pending" },
  { value: "scheduled", label: "pickups.status.scheduled" },
  { value: "in_progress", label: "pickups.status.inProgress" },
  { value: "completed", label: "pickups.status.completed" },
  { value: "cancelled", label: "pickups.status.cancelled" },
];

/**
 * Status colors for pickup badges
 */
export const PICKUP_STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  scheduled: "bg-blue-100 text-blue-800 border-blue-200",
  in_progress: "bg-purple-100 text-purple-800 border-purple-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  cancelled: "bg-red-100 text-red-800 border-red-200",
};

// ============================================================================
// MANAGEMENT PAGE FILTER OPTIONS
// ============================================================================

/**
 * Pickup management page filter options
 */
export const PICKUP_MANAGEMENT_STATUS_FILTERS = [
  { value: "all", label: "pickups.filters.allPickups" },
  { value: "pending", label: "pickups.filters.pending" },
  { value: "approved", label: "pickups.filters.approved" },
  { value: "rejected", label: "pickups.filters.rejected" },
  { value: "picked_up", label: "pickups.filters.pickedUp" },
  { value: "in_transit", label: "pickups.filters.inTransit" },
  { value: "signed", label: "pickups.filters.signed" },
  { value: "failed", label: "pickups.filters.failed" },
];

export const PICKUP_MANAGEMENT_TYPE_FILTERS = [
  { value: "all", label: "pickups.filters.allTypes" },
  { value: "package", label: "pickups.filters.packagePickup" },
  { value: "product", label: "pickups.filters.productPickup" },
];

export const PICKUP_MANAGEMENT_ASSIGNMENT_FILTERS = [
  { value: "all", label: "pickups.filters.allAssignments" },
  { value: "assigned", label: "pickups.filters.assigned" },
  { value: "unassigned", label: "pickups.filters.unassigned" },
];

// ============================================================================
// PICKUP TIME SLOTS
// ============================================================================

/**
 * Available pickup time slots
 * Use translation keys for labels: t(`pickups.timeSlot.${value}`)
 */
export const PICKUP_TIME_SLOTS = [
  { value: "09:00-12:00", label: "pickups.timeSlot.morning" },
  { value: "12:00-15:00", label: "pickups.timeSlot.midday" },
  { value: "15:00-18:00", label: "pickups.timeSlot.afternoon" },
  { value: "18:00-21:00", label: "pickups.timeSlot.evening" },
];

// ============================================================================
// FORM FIELD NAMES
// ============================================================================

/**
 * Pickup form field names
 */
export const PICKUP_FORM_FIELDS = {
  ADDRESS: "address",
  CITY: "city",
  PHONE: "phone",
  CONTACT_NAME: "contactName",
  PICKUP_DATE: "pickupDate",
  TIME_SLOT: "timeSlot",
  NOTES: "notes",
  PACKAGE_COUNT: "packageCount",
  STATUS: "status",
  ASSIGNED_LIVREUR: "assignedLivreur",
};

// ============================================================================
// VALIDATION RULES
// ============================================================================

/**
 * Pickup validation constants
 */
export const PICKUP_VALIDATION = {
  ADDRESS_MIN_LENGTH: 5,
  ADDRESS_MAX_LENGTH: 200,
  PHONE_MIN_LENGTH: 10,
  PHONE_MAX_LENGTH: 10,
  MIN_PACKAGE_COUNT: 1,
  MAX_PACKAGE_COUNT: 1000,
  NOTES_MAX_LENGTH: 500,
};

// ============================================================================
// TOAST MESSAGE KEYS
// ============================================================================

/**
 * Toast message translation keys for pickup operations
 */
export const PICKUP_TOAST_KEYS = {
  CREATING: "toast.creatingPickup",
  CREATED_SUCCESS: "toast.pickupCreated",
  UPDATE_SUCCESS: "toast.pickupUpdated",
  DELETE_SUCCESS: "toast.pickupDeleted",
  STATUS_UPDATE_SUCCESS: "pickups.statusUpdated",
  ASSIGNMENT_SUCCESS: "pickups.assignmentSuccess",
  SCHEDULE_SUCCESS: "pickups.scheduleSuccess",
  GENERATING_BON: 'pickups.generatingBon',
  BON_GENERATED: 'pickups.bonGenerated',
  BON_GENERATION_ERROR: 'pickups.bonGenerationError',
};

// ============================================================================
// ROUTE PATHS
// ============================================================================

/**
 * Pickup-related route paths
 */
export const PICKUP_ROUTES = {
  LIST: "/pickups",
  CREATE: "/pickups/create",
  DETAILS: "/pickups/:id",
  DASHBOARD: "/pickups/dashboard",
  MANAGEMENT: "/pickups/management",
  MY_PICKUPS: "/pickups/my-pickups",
};

// ============================================================================
// FILTER CONFIGURATION
// ============================================================================

/**
 * Pickup filter configuration
 */
export const PICKUP_FILTER_CONFIG = {
  showStatusFilter: true,
  showCityFilter: true,
  showDateRangeFilter: true,
  showLivreurFilter: true,
  entityName: "pickups",
};

// ============================================================================
// PLACEHOLDERS
// ============================================================================

/**
 * Placeholder text for pickup form fields (translation keys)
 */
export const PICKUP_PLACEHOLDERS = {
  ADDRESS: "pickups.form.addressPlaceholder",
  CITY: "pickups.form.cityPlaceholder",
  PHONE: "0600000000",
  CONTACT_NAME: "pickups.form.contactNamePlaceholder",
  NOTES: "pickups.form.notesPlaceholder",
  PACKAGE_COUNT: "pickups.form.packageCountPlaceholder",
};

// ============================================================================
// QUERY KEYS
// ============================================================================

/**
 * React Query keys for pickup-related queries
 */
export const PICKUP_QUERY_KEYS = {
  BASE: "pickups",
  DETAIL: "pickup",
  MY_PICKUPS: "my-pickups",
  SCHEDULED: "scheduled-pickups",
  LIVREUR_PICKUPS: "livreur-pickups",
  DASHBOARD: "pickups-dashboard",
};

// ============================================================================
// EXPORT CONFIGURATION
// ============================================================================

/**
 * Pickup export configuration
 */
export const PICKUP_EXPORT_CONFIG = {
  FILE_NAME_PREFIX: "pickups",
  DATE_FORMAT: "yyyy-MM-dd",
  SHEET_NAME: "Pickups",
};

// ============================================================================
// PAGINATION & LIMITS
// ============================================================================

/**
 * Pickup list pagination settings
 */
export const PICKUP_PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  PAGE_SIZE_OPTIONS: [10, 20, 50, 100],
};
